// This file is the entry point for Vite
import './versionGuard';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { AppProviders } from './components/providers/AppProviders';

// Import font
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

// Import styles
import './styles/globals.css';
import './styles/tailwind.css';
import './styles/admin-cards.css';

// More comprehensive error fallback with additional troubleshooting info
function ErrorFallback({ error, resetErrorBoundary }: { error: Error, resetErrorBoundary?: () => void }) {
  console.error("Application error:", error);
  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#fff', 
      color: '#333',
      fontFamily: 'Inter, system-ui, sans-serif',
      maxWidth: '800px',
      margin: '0 auto',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      marginTop: '40px'
    }}>
      <h1 style={{ color: '#e11d48', marginBottom: '16px' }}>Application Error</h1>
      <p style={{ marginBottom: '16px' }}>The application encountered an error:</p>
      <pre style={{ 
        backgroundColor: '#f9fafb', 
        padding: '12px', 
        borderRadius: '6px',
        overflowX: 'auto',
        fontSize: '14px',
        lineHeight: 1.5,
        marginBottom: '16px'
      }}>
        {error.message}
        {error.stack && `\n\nStack trace:\n${error.stack.split('\n').slice(1, 5).join('\n')}`}
      </pre>
      
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>Troubleshooting Steps</h2>
        <ul style={{ paddingLeft: '20px', lineHeight: 1.6 }}>
          <li>Try clearing your browser cache</li>
          <li>Check console for additional errors (F12 &gt; Console tab)</li>
          <li>Ensure all dependencies are properly loaded</li>
          <li>Try disabling browser extensions</li>
        </ul>
      </div>
      
      <button 
        onClick={() => {
          console.log("Attempting to reload application");
          if (resetErrorBoundary) {
            resetErrorBoundary();
          } else {
            window.location.reload();
          }
        }}
        style={{
          backgroundColor: '#2563eb',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer',
          fontWeight: 500
        }}
      >
        Reload Application
      </button>
    </div>
  );
}

function initializeApp() {
  console.log('Initializing application...');
  
  // Check if root element exists
  let rootElement = document.getElementById('root');
  
  // If root element doesn't exist, create it
  if (!rootElement) {
    console.warn('Root element not found, creating one...');
    rootElement = document.createElement('div');
    rootElement.id = 'root';
    document.body.appendChild(rootElement);
  }
  
  try {
    // Create root and render app with explicit error boundaries
    const root = ReactDOM.createRoot(rootElement);
    
    // Wrap in basic error handler first
    const AppWithErrorHandling = () => {
      try {
        return (
          <React.StrictMode>
            <React.Suspense fallback={<div className="p-4 text-center">Loading application...</div>}>
              <AppProviders>
                <App />
              </AppProviders>
            </React.Suspense>
          </React.StrictMode>
        );
      } catch (error) {
        console.error("Error in AppWithErrorHandling:", error);
        return <ErrorFallback error={error as Error} />;
      }
    };
    
    // Render with additional error catching
    try {
      root.render(<AppWithErrorHandling />);
      console.log('Application initialized successfully');
    } catch (error) {
      console.error('Root render failed:', error);
      renderErrorFallback(error as Error);
    }
  } catch (error) {
    console.error('Failed to initialize React root:', error);
    renderErrorFallback(error as Error);
  }
}

function renderErrorFallback(error: Error) {
  // Create fallback root if needed
  let fallbackRoot = document.getElementById('root');
  if (!fallbackRoot) {
    fallbackRoot = document.createElement('div');
    fallbackRoot.id = 'root';
    document.body.appendChild(fallbackRoot);
  }
  
  try {
    // Render error fallback
    console.log('Rendering error fallback UI');
    const fallbackRootInstance = ReactDOM.createRoot(fallbackRoot);
    fallbackRootInstance.render(<ErrorFallback error={error} />);
  } catch (finalError) {
    // Last resort plain HTML error display if React fails completely
    console.error('Critical failure in error fallback:', finalError);
    fallbackRoot.innerHTML = `
      <div style="padding: 20px; font-family: sans-serif;">
        <h1 style="color: #e11d48;">Critical Error</h1>
        <p>The application failed to load. Please try again later.</p>
        <pre style="background: #f5f5f5; padding: 10px;">${error.message}</pre>
        <button onclick="window.location.reload()">Reload</button>
      </div>
    `;
  }
}

// Use a timeout to ensure DOM is fully loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  // Immediate execution with console output for debugging
  console.log('DOM already loaded, initializing app immediately');
  initializeApp();
}
